import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API } from 'api';
import { isNil } from 'lodash-es';
import { WeatherStationObservation } from '../../weatherStations.types';

type UseWeatherStationObservationsProps = {
  id: string | number;
  start: Date;
  end: Date;
  pollInterval?: number;
};

type UseWeatherStationObservations = Omit<
  UseQueryResult<WeatherStationObservation[], Error>,
  'data'
> & {
  observations: WeatherStationObservation[];
};

type ServerWeatherStationObservation = {
  timestamp: string;
  values: {
    airTemp?: number;
    dewPointTemperature?: number;
    relativeHumidity?: number;
    windDirection?: number;
    windGust?: number;
    windSpeed?: number;
  };
};

type FetchObservationsValues = {
  id: string | number;
  start: Date;
  end: Date;
};

/**
 * Synoptic wind direction refers to the direction the wind is coming FROM,
 * so we need to reverse it for displaying the wind arrow correctly.
 */
export const getReverseDirection = (degrees: number): number => {
  const reverseDegrees = (degrees + 180) % 360;
  return reverseDegrees < 0 ? reverseDegrees + 360 : reverseDegrees;
};

const fetchWeatherStationObservations = async (
  values: FetchObservationsValues,
): Promise<WeatherStationObservation[]> => {
  const { id, start, end } = values;
  const response = await API.get<ServerWeatherStationObservation[]>(
    `weather/observations/${id}/`,
    {
      params: {
        start: start.toISOString(),
        end: end.toISOString(),
      },
    },
  );

  return response.data.map((observation) => {
    const windDirectionToward = !isNil(observation.values.windDirection)
      ? getReverseDirection(observation.values.windDirection)
      : undefined;
    return {
      timestamp: new Date(observation.timestamp),
      ...observation.values,
      windDirectionToward,
    };
  });
};

export const useWeatherStationObservations = (
  props: UseWeatherStationObservationsProps,
): UseWeatherStationObservations => {
  const { id, start, end, pollInterval } = props;

  const { data: observations = [], ...results } = useQuery({
    queryKey: ['weatherStationObservations', id],
    queryFn: () => fetchWeatherStationObservations({ id, start, end }),
    refetchInterval: pollInterval,
  });

  return {
    ...results,
    observations,
  };
};
